import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import UIKitDropDown from './DropDown';
import { spacingPx } from '../theme/tokens.spacing';
import { colors } from '../theme/tokens.colors';
import { DropDownStyleProps } from './DropDownShell';
import UIKitIconButton from './IconButton';

interface Props {
  pageSize: number;
  pageNumber: number;
  total: number;
  onChangePage: (value: number) => void;
  onChangeSize?: (value: number) => void;
}

const Root = styled('div')({
  padding: spacingPx[12],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: spacingPx[16],
  backgroundColor: '#fff',
});

const RowsPerPageLabel = styled('span')({
  marginRight: spacingPx[8],
  fontSize: 12,
  color: colors.neutral[700],
});

const Legend = styled('span')({
  fontSize: 12,
  color: colors.neutral[700],
  marginLeft: spacingPx[16],
  marginRight: spacingPx[16],
});

const Actions = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  '& > * + *': {
    marginLeft: spacingPx[4],
  },
});

const UIKitTablePagination: React.FC<Props> = ({
  pageSize,
  pageNumber,
  total,
  onChangeSize,
  onChangePage,
}) => {
  const { t } = useTranslation();

  if (total === 0) {
    return null;
  }

  const pageSizes = [
    { id: 5, text: '5' },
    { id: 10, text: '10' },
    { id: 15, text: '15' },
  ];
  // fontsize/fontweight ya fuerzan explícitamente el font-size de valueText
  // en DropDownShell - la vieja className `dropdownFontSize` (fontSize: 12)
  // que se pasaba acá era redundante con esto (mismo valor, aplicado un
  // nivel más arriba, sólo relevante por herencia).
  const dropDownSize: DropDownStyleProps = {
    height: 40,
    minHeight: 40,
    minWidth: 84,
    width: 84,
    fontsize: 12,
    fontweight: 600,
  };
  const from = (pageNumber - 1) * pageSize + 1;
  const isLastPage = total <= pageNumber * pageSize;
  const to = isLastPage ? total : pageNumber * pageSize;

  return (
    <Root>
      {onChangeSize && (
        <>
          <RowsPerPageLabel>{t('rowsPerPage')}</RowsPerPageLabel>
          <UIKitDropDown
            dropDownStyles={dropDownSize}
            elements={pageSizes}
            value={pageSize}
            onChange={(ps: number) => onChangeSize(ps)}
          />
        </>
      )}

      <Legend>{t('pageLegend', { from, to, total })}</Legend>

      <Actions>
        <UIKitIconButton
          iconName="chevron_left"
          size="small"
          variant="standard"
          disabled={pageNumber === 1}
          onClick={() => onChangePage(pageNumber - 1)}
          title={t('previousPage')}
        />
        <UIKitIconButton
          iconName="chevron_right"
          size="small"
          variant="standard"
          disabled={isLastPage}
          onClick={() => onChangePage(pageNumber + 1)}
          title={t('nextPage')}
        />
      </Actions>
    </Root>
  );
};

export default React.memo(
  UIKitTablePagination,
  (prev, next) =>
    prev.pageSize === next.pageSize &&
    prev.pageNumber === next.pageNumber &&
    prev.total === next.total
);
